import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Form,
  Spinner,
  Table,
  Alert,
} from "react-bootstrap";
import { FaSyncAlt, FaSearch } from "react-icons/fa";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";

const IECComponent = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [abhiyanList, setAbhiyanList] = useState([]);
  const [districtList, setDistrictList] = useState([]);
  const [talukaList, setTalukaList] = useState([]);
  const [villageList, setVillageList] = useState([]);

  const [selectedAbhiyan, setSelectedAbhiyan] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState([]);
  const [selectedTaluka, setSelectedTaluka] = useState([]);
  const [selectedVillage, setSelectedVillage] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [openDropdown, setOpenDropdown] = useState(null);
  const navigate = useNavigate();
  const apiBaseUrl = process.env.REACT_APP_API_URL;

  /* ================= CHECKBOX DROPDOWN ================= */
  const CheckboxDropdown = ({
    label,
    options,
    idKey,
    nameKey,
    selected,
    setSelected,
    disabled,
    dropdownKey,
  }) => {
    const toggle = () => {
      if (!disabled)
        setOpenDropdown(openDropdown === dropdownKey ? null : dropdownKey);
    };

    return (
      <Form.Group className="mb-3 position-relative">
        <Form.Label>{label}</Form.Label>

        <div
          className={`form-select ${disabled ? "disabled" : ""}`}
          style={{ cursor: disabled ? "not-allowed" : "pointer" }}
          onClick={toggle}
        >
          {selected.length === options.length && options.length > 0
            ? "All Selected"
            : selected.length > 0
            ? `${selected.length} Selected`
            : "All"}
        </div>

        {openDropdown === dropdownKey && !disabled && (
          <div
            className="position-absolute bg-white border rounded shadow-sm w-100"
            style={{ zIndex: 9999, maxHeight: 220, overflowY: "auto", bottom: "100%", marginBottom: "8px", top: "auto" }}
          >
            <div className="px-3 py-2 border-bottom fw-bold">
              <Form.Check
                type="checkbox"
                label="Select All"
                checked={selected.length === options.length}
                onChange={(e) =>
                  setSelected(e.target.checked ? options.map(o => o[idKey]) : [])
                }
              />
            </div>

            {options.map(o => (
              <div key={o[idKey]} className="px-3 py-2">
                <Form.Check
                  type="checkbox"
                  label={o[nameKey]}
                  checked={selected.includes(o[idKey])}
                  onChange={() =>
                    setSelected(
                      selected.includes(o[idKey])
                        ? selected.filter(x => x !== o[idKey])
                        : [...selected, o[idKey]]
                    )
                  }
                />
              </div>
            ))}
          </div>
        )}
      </Form.Group>
    );
  };

  // 📋 Column Display Name Map (Marathi / Friendly names)
  const columnHeaderMap = {
    
    bankers_meet_count: "बँकर्स मिटिंग",
    banner_count: "बॅनर लावण्यात आले",
    bhoomi_poojan_count: "भूमी पूजन कार्यक्रम",
    brochure_count: "ब्रॉशर वितरीत केले",
    gruhpravesh_count: "गृहप्रवेश कार्यक्रम",
    pamphlet_count: "पत्रके वितरित केली",
    poster_count: "पोस्टर लावली",
    price_distribution_count: "पुरस्कार वितरण कार्यक्रम",
    womens_day_celebration_count: "महिला दिन कार्यक्रम",
    
  };

  /* -----------------------------------------------------------
      1️⃣ LOAD ABHIYANS
  ----------------------------------------------------------- */
  const fetchAbhiyans = useCallback(async () => {
    try {
      const res = await fetch(`${apiBaseUrl}/iec/abhiyans`);
      const json = await res.json();
      setAbhiyanList(Array.isArray(json) ? json : []);
    } catch (err) {
      console.error("Error fetching Abhiyans:", err);
      setError(err.message);
    }
  }, [apiBaseUrl]);

  /* -----------------------------------------------------------
      2️⃣ LOAD INITIAL DATA (ALL DISTRICTS)
  ----------------------------------------------------------- */
  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBaseUrl}/iec/districts`);
      const json = await res.json();
      setData(Array.isArray(json) ? json : []);
    } catch (err) {
      console.error("Error fetching initial data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl]);

  /* -----------------------------------------------------------
      3️⃣ WHEN ABHIYAN SELECTED → GET DISTRICTS
  ----------------------------------------------------------- */
  const fetchDistrictsByAbhiyan = useCallback(
    async (abhiyanIds) => {
      if (!abhiyanIds || abhiyanIds.length === 0) return;
      setLoading(true);
      try {
        const res = await fetch(`${apiBaseUrl}/iec/abhiyans/${abhiyanIds.join(",")}/districts`);
        const json = await res.json();
        setData(Array.isArray(json) ? json : []);
        setDistrictList(Array.isArray(json) ? json : []);
        setTalukaList([]);
        setVillageList([]);
        setSelectedDistrict([]);
        setSelectedTaluka([]);
        setSelectedVillage([]);
      } catch (err) {
        console.error("Error fetching districts:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [apiBaseUrl]
  );

  /* -----------------------------------------------------------
      4️⃣ WHEN DISTRICT SELECTED → GET TALUKAS
  ----------------------------------------------------------- */
  const fetchTalukasByDistrict = useCallback(
    async (abhiyanIds, districtIds) => {
      if (!abhiyanIds || abhiyanIds.length === 0 || !districtIds || districtIds.length === 0) return;
      setLoading(true);
      try {
        const res = await fetch(
          `${apiBaseUrl}/iec/abhiyans/${abhiyanIds.join(",")}/districts/${districtIds.join(",")}/talukas`
        );
        const json = await res.json();
        setTalukaList(Array.isArray(json) ? json : []);
        setData(Array.isArray(json) ? json : []);
        setVillageList([]);
        setSelectedTaluka([]);
        setSelectedVillage([]);
      } catch (err) {
        console.error("Error fetching talukas:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [apiBaseUrl]
  );

  /* -----------------------------------------------------------
      5️⃣ WHEN TALUKA SELECTED → GET VILLAGES
  ----------------------------------------------------------- */
  const fetchVillagesByTaluka = useCallback(
    async (abhiyanIds, districtIds, talukaIds) => {
      if (!abhiyanIds || abhiyanIds.length === 0 || !districtIds || districtIds.length === 0 || !talukaIds || talukaIds.length === 0) return;
      setLoading(true);
      try {
        const res = await fetch(
          `${apiBaseUrl}/iec/abhiyans/${abhiyanIds.join(",")}/districts/${districtIds.join(",")}/talukas/${talukaIds.join(",")}/villages`
        );
        const json = await res.json();
        setVillageList(Array.isArray(json) ? json : []);
        setData(Array.isArray(json) ? json : []);
        setSelectedVillage([]);
      } catch (err) {
        console.error("Error fetching villages:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [apiBaseUrl]
  );

  /* -----------------------------------------------------------
      6️⃣ WHEN VILLAGE SELECTED → FILTER ONLY THAT VILLAGE
  ----------------------------------------------------------- */
  useEffect(() => {
    if (selectedVillage.length > 0 && villageList.length > 0) {
      const filtered = villageList.filter((v) => selectedVillage.includes(v.village_id));
      setData(Array.isArray(filtered) ? filtered : []);
    }
  }, [selectedVillage, villageList]);

  /* -----------------------------------------------------------
      7️⃣ REFRESH ALL
  ----------------------------------------------------------- */
  const handleRefresh = () => {
    setSelectedAbhiyan([]);
    setSelectedDistrict([]);
    setSelectedTaluka([]);
    setSelectedVillage([]);
    setDistrictList([]);
    setTalukaList([]);
    setVillageList([]);
    setSearchTerm("");
    fetchInitialData();
  };

  /* -----------------------------------------------------------
      8️⃣ EXPORT EXCEL
  ----------------------------------------------------------- */
  const exportToExcel = () => {
    if (filteredView.length === 0) return;

    const areaColumnName =
      selectedVillage.length > 0 ? "गाव" : selectedTaluka.length > 0 ? "गाव" : selectedDistrict.length > 0 ? "तालुका" : "जिल्हा";

    const exportData = filteredView.map((item, index) => ({
      "अ. क्र": index + 1,
      [areaColumnName]: selectedVillage.length > 0
        ? item.village_name
        : selectedTaluka.length > 0
        ? item.village_name
        : selectedDistrict.length > 0
        ? item.taluka_name
        : item.district_name,
      ...Object.keys(columnHeaderMap).reduce((acc, key) => {
        acc[columnHeaderMap[key]] = item[key] || "0";
        return acc;
      }, {}),
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "IEC Report");
    XLSX.writeFile(wb, "IEC_Report.xlsx");
  };

  /* -----------------------------------------------------------
      9️⃣ CALCULATE TOTALS
  ----------------------------------------------------------- */
  const calculateTotals = (items) => {
    const totals = {};
    Object.keys(columnHeaderMap).forEach((col) => {
      totals[col] = items.reduce((sum, row) => sum + (parseInt(row[col]) || 0), 0);
    });
    return totals;
  };

  /* -----------------------------------------------------------
      LOAD INITIAL DATA
  ----------------------------------------------------------- */
  useEffect(() => {
    fetchAbhiyans();
    fetchInitialData();
  }, [fetchAbhiyans, fetchInitialData]);

  /* -----------------------------------------------------------
      TRIGGER FETCH WHEN FILTERS CHANGE
  ----------------------------------------------------------- */
  useEffect(() => {
    if (selectedAbhiyan.length > 0) fetchDistrictsByAbhiyan(selectedAbhiyan);
  }, [selectedAbhiyan, fetchDistrictsByAbhiyan]);

  useEffect(() => {
    if (selectedDistrict.length > 0 && selectedAbhiyan.length > 0)
      fetchTalukasByDistrict(selectedAbhiyan, selectedDistrict);
  }, [selectedDistrict, selectedAbhiyan, fetchTalukasByDistrict]);

  useEffect(() => {
    if (selectedTaluka.length > 0 && selectedAbhiyan.length > 0 && selectedDistrict.length > 0)
      fetchVillagesByTaluka(selectedAbhiyan, selectedDistrict, selectedTaluka);
  }, [selectedTaluka, selectedAbhiyan, selectedDistrict, fetchVillagesByTaluka]);

  /* -----------------------------------------------------------
      SEARCH FILTER
  ----------------------------------------------------------- */
  const filteredView = data.filter((item) =>
    Object.values(item).join(" ").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totals = calculateTotals(filteredView);

  /* -----------------------------------------------------------
      GET DYNAMIC COLUMNS
  ----------------------------------------------------------- */
  const getDynamicColumns = (items) => {
    if (!Array.isArray(items) || items.length === 0) return [];
    return Object.keys(items[0]).filter(
      (key) => !["district_name", "taluka_name", "village_name", "district_id", "taluka_id", "village_id"].includes(key)
    );
  };

  const dynamicColumns = getDynamicColumns(filteredView);

  return (
    <Container fluid>
      <Row>
        {/* LEFT FILTER PANEL */}
        <Col md={2} className="p-4">
          <Button variant="secondary" onClick={() => navigate("/")} className="mb-3 w-100">
            Back
          </Button>

          <div className="p-3 rounded shadow-sm" style={{ backgroundColor: "#EAF0F9" }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="fw-bold" style={{ color: "#1F3C88", margin: 0 }}>Filters</h5>
              <div
                onClick={handleRefresh}
                style={{
                  backgroundColor: "#EAF2FF",
                  borderRadius: "50%",
                  padding: "6px",
                  cursor: "pointer",
                }}
              >
                <FaSyncAlt size={16} style={{ color: "#1F3C88" }} />
              </div>
            </div>

            <CheckboxDropdown 
              label="अभियान" 
              options={abhiyanList}
              idKey="abhiyan_id" 
              nameKey="abhiyan_name"
              selected={selectedAbhiyan} 
              setSelected={setSelectedAbhiyan} 
              dropdownKey="abhiyan" 
            />

            <CheckboxDropdown 
              label="जिल्हा" 
              options={districtList}
              idKey="district_id" 
              nameKey="district_name"
              selected={selectedDistrict} 
              setSelected={setSelectedDistrict}
              disabled={!districtList.length} 
              dropdownKey="district" 
            />

            <CheckboxDropdown 
              label="तालुका" 
              options={talukaList}
              idKey="taluka_id" 
              nameKey="taluka_name"
              selected={selectedTaluka} 
              setSelected={setSelectedTaluka}
              disabled={!talukaList.length} 
              dropdownKey="taluka" 
            />

            <CheckboxDropdown 
              label="गाव" 
              options={villageList}
              idKey="village_id" 
              nameKey="village_name"
              selected={selectedVillage} 
              setSelected={setSelectedVillage}
              disabled={!villageList.length} 
              dropdownKey="village" 
            />
          </div>
        </Col>

        {/* MAIN TABLE */}
        <Col md={10} className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap" style={{ gap: "10px" }}>
            <h5 className="fw-bold mb-0" style={{ color: "#1F3C88", fontSize: "18px" }}>
              IEC Activity Report
            </h5>

            <div className="d-flex align-items-center" style={{ gap: "10px" }}>
              <Form.Group controlId="searchBox" style={{ width: "250px", margin: 0 }}>
                <div className="d-flex align-items-center border rounded px-2">
                  <FaSearch style={{ color: "#6c757d", marginRight: "8px" }} />
                  <Form.Control
                    type="text"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      border: "none",
                      outline: "none",
                      boxShadow: "none",
                      height: "36px",
                      fontSize: "14px",
                    }}
                  />
                </div>
              </Form.Group>

              <Button
                onClick={exportToExcel}
                style={{
                  backgroundColor: "#4da6ff",
                  borderColor: "#4da6ff",
                  color: "#fff",
                  height: "42px",
                  fontSize: "14px",
                  minWidth: "130px",
                  borderRadius: "6px",
                  border: "none",
                }}
              >
                Export Excel
              </Button>
            </div>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}
          {loading ? (
            <Spinner animation="border" className="d-block mx-auto mt-5" />
          ) : (
            <div style={{ maxHeight: "500px", overflowY: "auto", border: "1px solid #dee2e6", borderRadius: "6px" }}>
              <Table striped bordered hover responsive>
                <thead style={{ position: "sticky", top: 0, backgroundColor: "#f8f9fa" }}>
                  <tr>
                    <th className="text-center align-middle">अ. क्र</th>
                    <th className="text-center align-middle">
                      {selectedVillage.length > 0 ? "गाव" : selectedTaluka.length > 0 ? "गाव" : selectedDistrict.length > 0 ? "तालुका" : "जिल्हा"}
                    </th>
                    {dynamicColumns.map((col) => (
                      <th key={col} className="text-center align-middle">
                        {columnHeaderMap[col] || col.replace(/_/g, " ").toUpperCase()}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {/* TOTAL ROW FIRST, as in InnovationOne.js */}
                  {filteredView.length > 0 && (
                    <tr style={{ backgroundColor: "#eef3ff", fontWeight: "bold" }}>
                      <td colSpan={2} style={{ textAlign: "center" }}>
                        Total
                      </td>
                      {dynamicColumns.map((col) => (
                        <td key={col} className="text-center align-middle">
                          {totals[col]}
                        </td>
                      ))}
                    </tr>
                  )}

                  {filteredView.length > 0 ? (
                    filteredView.map((item, idx) => (
                      <tr key={idx}>
                        <td className="text-center align-middle">{idx + 1}</td>
                        <td className="text-center align-middle">
                          {selectedVillage.length > 0 ? item.village_name : selectedTaluka.length > 0 ? item.village_name : selectedDistrict.length > 0 ? item.taluka_name : item.district_name}
                        </td>
                        {dynamicColumns.map((col) => (
                          <td key={col} className="text-center align-middle">
                            {item[col] || "0"}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={2 + dynamicColumns.length} className="text-center">
                        No Records Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default IECComponent;
